import {
  Button,
  Cascader,
  Divider,
  Form,
  Modal,
  Select,
  Space,
  Image,
} from 'antd';
import {
  formInitialValues,
  getGroupBuyingItemsForSelectOptions,
  getHouseholdNumberForCascaderOptions,
  getOrderableAmountsOptions,
  getResidentsForSelectOptions,
  getSelectedItem,
  handleSubmit,
} from './helper';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { NotificationOutlined } from '@ant-design/icons';
import client from '@/lib/feathers';
import type { Option } from '@/utils/generateHouseHoldNumberCascader';
import type { ResidentAccountNumber } from '@/types/database/residentAccountNumbers';
import type { GroupBuying } from '@/types/database/groupBuying';
import type { User } from '@/types/database/users';
import useService from '@/lib/hooks/useService';

import { GroupBuyingOrdersContext } from '../OrderManagement';
import type { AddOrderFormValues } from '../types';
import styles from './index.module.scss';

const iconStyle = {
  fontSize: '17px',
  color: '#209BFA',
};

const AddOrder = () => {
  const [residentAccountNumbers] = useService<ResidentAccountNumber>({
    collectionName: 'residentAccountNumbers',
  });
  const [groupBuyingItems] = useService<GroupBuying>({
    collectionName: 'groupBuying',
    query: {
      status: '待成團',
    },
  });
  const groupBuyingOrdersService = client.service('groupBuyingOrders');

  const [users] = useService<User>({
    collectionName: 'users',
  });

  const { currentOrders, updateOrders } = useContext(GroupBuyingOrdersContext);

  const [form] = Form.useForm<AddOrderFormValues>();

  const [isOpenAddOrderModal, setIsOpenAddOrderModal] = useState(false);
  const [isGroupBuyingSelect, setIsGroupBuyingSelect] = useState(false);
  const [residentNamesForSelectOptions, setResidentNamesForSelectOptions] =
    useState<Option[]>([]);
  const [selectedGroupBuyingItem, setSelectedGroupBuyingItem] =
    useState<GroupBuying | null>();

  const closeAddOrderModal = () => {
    form.resetFields();
    setIsOpenAddOrderModal(false);
    setIsGroupBuyingSelect(false);
    setSelectedGroupBuyingItem(null);
    setResidentNamesForSelectOptions([]);
  };

  const groupBuyingItemsForSelectOptions = useMemo(
    () => getGroupBuyingItemsForSelectOptions(groupBuyingItems),
    [groupBuyingItems]
  );

  const householdNumberForCascaderOptions = useMemo(
    () => getHouseholdNumberForCascaderOptions(residentAccountNumbers),
    [residentAccountNumbers]
  );

  const handleFormValueChange = (
    changedValue: { [key: string]: string },
    allValues: AddOrderFormValues
  ) => {
    if (changedValue.groupBuyingItemName) {
      setIsGroupBuyingSelect(true);
      if (isGroupBuyingSelect) {
        setSelectedGroupBuyingItem(getSelectedItem(groupBuyingItems, form));
      }
    } else if (changedValue.householdNumber) {
      form.setFieldValue('residentName', []);
      setResidentNamesForSelectOptions(
        getResidentsForSelectOptions(form, residentAccountNumbers) ?? []
      );
    } else if (!allValues.householdNumber) {
      form.setFieldValue('residentName', []);
      setResidentNamesForSelectOptions([]);
    }
  };

  useEffect(() => {
    if (isGroupBuyingSelect) {
      setSelectedGroupBuyingItem(getSelectedItem(groupBuyingItems, form));
    }
  }, [form, groupBuyingItems, isGroupBuyingSelect]);

  return (
    <>
      <Modal
        open={isOpenAddOrderModal}
        title="新增訂單"
        onCancel={closeAddOrderModal}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          scrollToFirstError
          onFinish={(values) =>
            handleSubmit({
              values,
              users,
              groupBuyingOrdersService,
              selectedGroupBuyingItem,
              updateOrders,
              closeAddOrderModal,
            })
          }
          onValuesChange={handleFormValueChange}
          initialValues={formInitialValues}
        >
          <Form.Item
            label="團購"
            name="groupBuyingItemName"
            rules={[{ required: true, message: '請選擇團購' }]}
          >
            <Select
              options={groupBuyingItemsForSelectOptions}
              placeholder="請選擇團購"
            />
          </Form.Item>
          <Form.Item
            label="戶號"
            name="householdNumber"
            rules={[{ required: true, message: '請選擇戶號' }]}
          >
            <Cascader
              options={householdNumberForCascaderOptions}
              placeholder="請選擇戶號"
            />
          </Form.Item>
          <Form.Item
            label="住戶"
            name="residentName"
            rules={[{ required: true, message: '請選擇住戶' }]}
          >
            <Select
              options={residentNamesForSelectOptions}
              placeholder="請選擇住戶"
            />
          </Form.Item>
          {isGroupBuyingSelect && (
            <>
              <Image
                width="100%"
                src={
                  selectedGroupBuyingItem?.imgUrl !== ''
                    ? selectedGroupBuyingItem?.imgUrl
                    : '/ezcon.png'
                }
                preview={false}
              />
              <Divider />
              <h1>{selectedGroupBuyingItem?.itemName}</h1>
              <Divider />
              {selectedGroupBuyingItem?.note !== '' && (
                <>
                  <Space direction="vertical">
                    <Space>
                      <NotificationOutlined style={iconStyle} />
                      <h4 className={styles.sectionTitle}>最新消息：</h4>
                    </Space>
                    <div>
                      <h5 className={styles.contentTitle}>最新消息</h5>
                      <p>{selectedGroupBuyingItem?.note}</p>
                    </div>
                  </Space>
                  <Divider />
                </>
              )}
              <Space direction="vertical">
                <Space>
                  <NotificationOutlined style={iconStyle} />
                  <h4 className={styles.sectionTitle}>團購資訊：</h4>
                </Space>
                <div>
                  <h5 className={styles.contentTitle}>團購起始時間</h5>
                  <p>
                    {selectedGroupBuyingItem &&
                      dayjs(selectedGroupBuyingItem?.startTime).format(
                        'YYYY/MM/DD HH:mm'
                      )}
                  </p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>團購截止時間</h5>
                  <p>
                    {selectedGroupBuyingItem &&
                      dayjs(selectedGroupBuyingItem?.endTime).format(
                        'YYYY/MM/DD HH:mm'
                      )}
                  </p>
                </div>
              </Space>
              <Divider />
              <Space direction="vertical">
                <Space>
                  <NotificationOutlined style={iconStyle} />
                  <h4 className={styles.sectionTitle}>商品資訊：</h4>
                </Space>
                <div>
                  <h5 className={styles.contentTitle}>賞味期限</h5>
                  <p>{selectedGroupBuyingItem?.expirationTime}</p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>過敏原</h5>
                  <p>{selectedGroupBuyingItem?.allergen}</p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>保存方法</h5>
                  <p>{selectedGroupBuyingItem?.storageMethod}</p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>廠商名稱</h5>
                  <p>{selectedGroupBuyingItem?.manufacturerName}</p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>廠商電話</h5>
                  <p>{selectedGroupBuyingItem?.manufacturerPhone}</p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>廠商地址</h5>
                  <p>{selectedGroupBuyingItem?.manufacturerAddress}</p>
                </div>
              </Space>
              <Divider />
              <Space direction="vertical">
                <Space>
                  <NotificationOutlined style={iconStyle} />
                  <h4 className={styles.sectionTitle}>商品資訊：</h4>
                </Space>
                <div>
                  <h5 className={styles.contentTitle}>商品介紹</h5>
                  <p>{selectedGroupBuyingItem?.itemIntroduction}</p>
                </div>
                <div>
                  <h5 className={styles.contentTitle}>商品包含</h5>
                  <p>{selectedGroupBuyingItem?.itemSpec}</p>
                </div>
              </Space>
              <Divider />
              <Space direction="vertical">
                <Space>
                  <NotificationOutlined style={iconStyle} />
                  <h4 className={styles.sectionTitle}>注意事項：</h4>
                </Space>
                <div>
                  <h5 className={styles.contentTitle}>注意事項</h5>
                  <p>{selectedGroupBuyingItem?.notice}</p>
                </div>
              </Space>
              <Divider />
              <Space direction="vertical" className={styles.orderPlanWrapper}>
                <Space>
                  <NotificationOutlined style={iconStyle} />
                  <h4 className={styles.sectionTitle}>團購方案：</h4>
                </Space>
                <div className={styles.planInfoContainer}>
                  <div>
                    <h5 className={styles.contentTitle}>方案</h5>
                    <p>{selectedGroupBuyingItem?.itemName}</p>
                  </div>
                  <div>
                    <h5 className={styles.contentTitle}>價格</h5>
                    <p>
                      {selectedGroupBuyingItem?.cost[0].amount
                        ? `NT$ ${selectedGroupBuyingItem?.cost[0].amount}`
                        : `${selectedGroupBuyingItem?.cost[1].amount}點`}
                    </p>
                  </div>
                  <div className={styles.purchaseAmountFormItemContainer}>
                    <Form.Item
                      label="數量"
                      name="purchaseAmount"
                      rules={[{ required: true, message: '請選擇數量' }]}
                    >
                      <Select
                        placeholder="請選擇數量"
                        options={
                          selectedGroupBuyingItem
                            ? getOrderableAmountsOptions({
                                maximumAmount:
                                  selectedGroupBuyingItem.maximumAmount,
                                currentOrders,
                                selectedGroupBuyingItem,
                              })
                            : [{ key: 0, label: 0, value: 0 }]
                        }
                      />
                    </Form.Item>
                  </div>
                </div>
              </Space>
            </>
          )}
          <Form.Item>
            <Space className={styles.formButtonsContainer}>
              <Button type="primary" onClick={closeAddOrderModal}>
                取消
              </Button>
              <Button htmlType="submit">確定</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Button onClick={() => setIsOpenAddOrderModal(true)}>新增訂單</Button>
    </>
  );
};

export default AddOrder;
